import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Breadcrumbs } from '@heroui/react/breadcrumbs';
import { Tooltip } from '@heroui/react/tooltip';
import { Separator } from '@heroui/react/separator';
import { ArrowLeft, PanelLeft, Maximize2, Minimize2 } from 'lucide-react';
import { cn } from '@/utils/cn';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { NotificationBell } from '@/components/notifications';
import { TourButton } from '@/components/tour/TourButton';
import { UserMenu, type ShellUser } from './UserMenu';
import { useBreadcrumbs } from './use-breadcrumbs';
import { usePageCrumbsOverride } from './use-page-crumbs';
import { useTitleScrolledAway } from './use-title-scrolled-away';
import { ClinicLogo } from '@/components/layout/ClinicSwitcher';
import { useAuth } from '@/contexts/AuthContext';
import { dashboardRoutePaths } from '@/routes/route-config';
import { useLanguage } from '@/contexts/LanguageContext';
import { useEdgeSwipeBack } from './use-edge-swipe-back';

/**
 * The top bar. Thin by policy: it carries only what is genuinely
 * global, and nothing that already exists in the sidebar.
 *
 * Removed from the old topbar: the "Reception" and "Doctor queue"
 * toggle buttons. Both destinations are sidebar items, so the bar was
 * duplicating navigation — and doing it with two different mental
 * models (the sidebar navigated, the buttons toggled in and out of a
 * mode). They now live in the Clinical section like every other
 * destination.
 *
 * HeroUI v3 has no Navbar component family (that was v2), so this is
 * composed from Breadcrumbs + Separator + Tooltip primitives.
 */

const iconButton = cn(
  'flex size-control shrink-0 items-center justify-center rounded-md',
  'text-secondary-500 transition-colors duration-(--shell-motion-fast)',
  'hover:bg-surface-2 hover:text-secondary-800',
  'outline-none focus-visible:ring-2 focus-visible:ring-primary/60',
);

export interface TopbarProps {
  user?: ShellUser | null;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
}

export function Topbar({
  user,
  collapsed,
  onToggleCollapsed,
  isFullscreen,
  onToggleFullscreen,
}: TopbarProps) {
  const { t } = useTranslation();
  // A detail page may publish a richer trail (patient name, and a parent link
  // that preserves the calendar day you came from). Otherwise the trail is
  // derived from the nav config.
  const derived = useBreadcrumbs();
  const override = usePageCrumbsOverride();
  const crumbs = override ?? derived;
  // Phones hide the trail, so a page below the top level gets an "up" control
  // in its place: back to the parent crumb, a known page (not `navigate(-1)`,
  // which leads somewhere different depending on how you arrived). The menu
  // stays one tap away under "More" in the tab bar.
  const parent = crumbs.length > 1 ? crumbs[crumbs.length - 2] : undefined;
  const titleAway = useTitleScrolledAway();
  const pageLabel = crumbs[crumbs.length - 1]?.label;
  const { clinic } = useAuth();
  const { isRTL } = useLanguage();
  // The same "up" as the arrow, by the iOS edge swipe (installed app only).
  useEdgeSwipeBack(parent?.href, isRTL);

  const toggleLabel = collapsed
    ? t('nav.expandSidebar', 'Expand sidebar')
    : t('nav.collapseSidebar', 'Collapse sidebar');

  return (
    <header
      className={cn(
        'sticky top-0 z-(--z-header) shrink-0 print:hidden',
        'border-b border-border-default bg-surface-1/85',
        'backdrop-blur-md supports-[backdrop-filter]:bg-surface-1/75',
      )}
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      <div className="flex h-(--shell-header-h) items-center gap-2 px-3 lg:gap-3 lg:px-6">
        {/*
          Tooltip.Trigger renders its own <div> rather than merging
          props into its child, so nesting a <button> inside it left
          the wrapper swallowing the press and the toggle did nothing.
          The trigger IS the button here, with `title` carrying the
          label instead.
        */}
        <Tooltip.Root delay={400}>
          <Tooltip.Trigger<'button'>
            render={(props) => (
              <button
                {...props}
                type="button"
                onClick={onToggleCollapsed}
                aria-label={toggleLabel}
                className={cn(iconButton, 'hidden lg:flex')}
              >
                <PanelLeft aria-hidden className="size-icon" />
              </button>
            )}
          />
          <Tooltip.Content className="text-small">
            {toggleLabel}
          </Tooltip.Content>
        </Tooltip.Root>

        {/* No menu button below `lg`: the tab bar is there, and its "More"
            opens the same menu. Two doors to one room read as a web page
            wearing an app's clothes. */}

        {parent?.href && (
          <Link
            to={parent.href}
            aria-label={t('nav.backTo', { page: parent.label })}
            className={cn(iconButton, 'md:hidden')}
          >
            <ArrowLeft aria-hidden className="size-5 rtl:rotate-180" />
          </Link>
        )}

        {/* Breadcrumbs answer "where am I" for someone landing deep. */}
        <Breadcrumbs className="hidden min-w-0 md:flex">
          {crumbs.map((crumb, i) => {
            const isLast = i === crumbs.length - 1;
            return (
              <Breadcrumbs.Item key={`${crumb.label}-${i}`}>
                {crumb.href && !isLast ? (
                  <Link
                    to={crumb.href}
                    className="no-underline hover:underline"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  crumb.label
                )}
              </Breadcrumbs.Item>
            );
          })}
        </Breadcrumbs>

        {/* Mobile: the leaf crumb alone, since the trail won't fit. Not a
            heading: the page's own PageHeader is the page's one <h1>. It
            shows once that h1 has scrolled away (large-title pattern), so
            the name is never on screen twice. */}
        {parent?.href ? (
          <p
            aria-hidden={!titleAway}
            className={cn(
              'min-w-0 truncate text-h3 font-semibold text-secondary-800 md:hidden',
              'transition-opacity duration-(--shell-motion-slide)',
              !titleAway && 'opacity-0',
            )}
          >
            {pageLabel}
          </p>
        ) : (
          // A top-level page has no "up", so the bar carries the clinic, as
          // a phone app carries its own name: the logo, and the clinic's
          // name until the page title scrolls under the bar, which then
          // takes its place. A tap goes home.
          <Link
            to={dashboardRoutePaths.home}
            className="flex min-w-0 items-center gap-2.5 ps-1 no-underline hover:no-underline md:hidden"
          >
            <ClinicLogo clinic={clinic} size={32} />
            <span className="grid min-w-0">
              <span
                aria-hidden={titleAway}
                className={cn(
                  '[grid-area:1/1] truncate text-h3 font-semibold text-secondary-800',
                  'transition-opacity duration-(--shell-motion-slide)',
                  titleAway && 'opacity-0',
                )}
              >
                {clinic?.name}
              </span>
              <span
                aria-hidden={!titleAway}
                className={cn(
                  '[grid-area:1/1] truncate text-h3 font-semibold text-secondary-800',
                  'transition-opacity duration-(--shell-motion-slide)',
                  !titleAway && 'opacity-0',
                )}
              >
                {pageLabel}
              </span>
            </span>
          </Link>
        )}

        <div className="flex-1" />

        {user && (
          <div className="flex shrink-0 items-center gap-0.5 sm:gap-1.5">
            {/* Only on a page that has a guided tour. */}
            <TourButton />

            <LanguageSwitcher />

            <button
              type="button"
              onClick={onToggleFullscreen}
              aria-label={
                isFullscreen
                  ? t('nav.exitFullscreen', 'Exit fullscreen')
                  : t('nav.enterFullscreen', 'Enter fullscreen')
              }
              className={cn(iconButton, 'hidden lg:flex')}
            >
              {isFullscreen ? (
                <Minimize2 aria-hidden className="size-icon" />
              ) : (
                <Maximize2 aria-hidden className="size-icon" />
              )}
            </button>

            <NotificationBell />

            <Separator
              orientation="vertical"
              className="mx-1 hidden h-6 lg:block"
            />

            <UserMenu user={user} />
          </div>
        )}
      </div>
    </header>
  );
}
